import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type UseMetricChartResult, useMetricChart } from '@/components/charts/generic';
import type { HealthWoundTrendPoint } from '@/domain/health';
import { parseLocale } from '@/utils/localeFormatters';

import type { WoundGraphSeriesConfig } from './healthWoundsGraphHelpers';
import { buildWoundGraphSpec } from './woundGraphSpec';

export interface UseHealthWoundGraphChartParams {
    trendPoints: HealthWoundTrendPoint[];
    woundSeries: WoundGraphSeriesConfig[];
}

/** A Health wound graph as a chart: the wound series in percent, the sea temperature behind. */
export function useHealthWoundGraphChart(
    params: UseHealthWoundGraphChartParams
): UseMetricChartResult<'line'> {
    const { trendPoints, woundSeries } = params;
    const { t, i18n } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    const spec = useMemo(
        () =>
            buildWoundGraphSpec({
                days: trendPoints.map((point) => point.day),
                series: woundSeries.map((series, index) => ({
                    key: `wound:${index}`,
                    label: series.label,
                    color: series.color,
                    values: trendPoints.map(series.getValue),
                })),
                temperature: trendPoints.map((point) => point.temperature),
                labels: {
                    axis: t('wounds.graphsAxisWounds'),
                    temperature: tGlobal('common.labels.temperature'),
                },
                locale: parseLocale(i18n.language),
            }),
        [trendPoints, woundSeries, t, tGlobal, i18n.language]
    );

    return useMetricChart(spec);
}
